import moment from "moment";
import { useIdentity } from "base/identity-resolver";
import { GlobalJourneyContext } from "../context/global-journey-context";
import { modifyJourneyTaskCounts } from "../utils/helpers";
import { useState, useEffect, useContext } from "react";
import {
  fetchJourneyGroups,
  fetchJourneys,
  fetchJourneyStepTasks,
  fetchJourneysForNextFive,
} from "../utils/api-requests/hooks/use-journey-requests";

const useJourneys = () => {
  const { globalJourney } = useContext(GlobalJourneyContext);
  const [journeyGroups, setJourneyGroups] = useState([]);
  const [selectedJourneyGroup, setSelectedJourneyGroup] = useState({});
  const [journeys, setJourneys] = useState([]);
  const [journeySteps, setJourneySteps] = useState([]);
  const [selectedJourney, setSelectedJourney] = useState({});
  const [journeysForMultipleDates, setJourneysForMultipleDates] = useState([]);
  const [selectedDueDate, setSelectedDueDate] = useState(moment.now());
  const [onStageWithTable, setOnStageWithTable] = useState(false);
  const [refetchJourneys, setRefetchJourneys] = useState(false);
  const [refetchJourneySteps, setRefetchJourneySteps] = useState(false);
  const [renderEmptyState, setRenderEmptyState] = useState(false);
  const { user } = useIdentity();
  const [closePrevDrawer, setClosePrevDrawer] = useState({ close: () => {} });
  const [selectedTask, setSelectedTask] = useState(null);
  const [loadingJourneys, setLoadingJourneys] = useState(false);

  // REFRESH SELECTED TASK (Right-side panel) when new journey is selected
  useEffect(() => {
    setSelectedTask(null);
  }, [selectedJourney]);

  // JOURNEY GROUPS AND SET SELECTED JOURNEY GROUP
  useEffect(() => {
    const getAndSetJourneyGroups = async () => {
      const journeyGroups = await fetchJourneyGroups();
      setJourneyGroups(journeyGroups);
      setSelectedJourneyGroup(journeyGroups[0] || {});
    };

    getAndSetJourneyGroups();
  }, []);

  // JOURNEYS FOR SELECTED JOURNEY GROUP
  useEffect(() => {
    const getAndSetJourneys = async () => {
      if (!Object.keys(selectedJourneyGroup).length) return;
      const { id: journeyGroupId } = selectedJourneyGroup;
      if (!journeyGroupId) {
        setRenderEmptyState(true);
        return;
      }

      setLoadingJourneys(true);
      const journeysList = await fetchJourneys(journeyGroupId, user.id, ["IN_PROGRESS"], selectedDueDate);
      setLoadingJourneys(false);
      setRenderEmptyState(!journeysList.length);
      setJourneys(journeysList);
      setRefetchJourneys(false);
    };

    getAndSetJourneys();
  }, [selectedJourneyGroup, globalJourney, selectedDueDate, user.id, refetchJourneys]);

  //CHECK FOR INITIAL LOAD AND IF SO DEFAULT TO INTRO
  useEffect(() => {
    const introJourney = journeys.find((j) => j.journey_type === "INTRODUCTION");
    if (!selectedJourney.id) {
      if (globalJourney.id) setSelectedJourney(globalJourney);
      else if (introJourney) setSelectedJourney(introJourney); // default to intro if we can
    }
  }, [journeys, selectedJourney, globalJourney]);

  // JOURNEYS TASKS FOR NEXT FIVE DATES
  useEffect(() => {
    const getAndSetJourneys = async () => {
      if (!Object.keys(selectedJourneyGroup).length) return;
      const journeysList = await fetchJourneysForNextFive(selectedJourneyGroup.id, user.id, ["IN_PROGRESS"]);
      setJourneysForMultipleDates(journeysList);
    };

    getAndSetJourneys();
  }, [selectedJourneyGroup, user.id]);

  // STEPS FOR JOURNEY
  useEffect(() => {
    const getAndSetJourneySteps = async () => {
      const statuses = ["IN_PROGRESS", "COMPLETED"];
      const steps = await fetchJourneyStepTasks(user.id, selectedJourney, selectedDueDate, statuses);
      setJourneySteps(steps);

      setRefetchJourneySteps(false);
    };

    getAndSetJourneySteps();
  }, [selectedJourney, selectedDueDate, user.id, refetchJourneySteps]);

  // OPEN STAGE BOOLEAN
  useEffect(() => {
    let showConstituentsTable;
    if (!selectedJourney.journey_type) showConstituentsTable = false;
    else showConstituentsTable = ["OPEN", "INACTIVE"].includes(selectedJourney.journey_type);
    setOnStageWithTable(showConstituentsTable);
  }, [selectedJourney]);

  // STATE MANAGEMENT FUNCTIONS

  const handleTaskCompletionStateUpdate = (step, task, completed_at) => {
    // When a task is marked complete, the following happens:
    // 1. the percentage bar at the top of the table is updated
    // 2. the number in the pill is either incremented or decremented
    // 3. the numbers in the Task Counter Card are incremented/decremented
    // Find & Copy
    const journeysCopy = [...journeys];
    const selectedJourneyCopy = journeysCopy.find((j) => j.id === selectedJourney.id);
    const journeyStepsCopy = [...journeySteps];
    const stepCopy = journeyStepsCopy.find((j) => j.id === step.id);
    const taskCopy = stepCopy.tasks.find((t) => t.id === task.id);

    // Modify In Place
    taskCopy.completed_at = completed_at;
    modifyJourneyTaskCounts(selectedJourneyCopy, completed_at, taskCopy.action_type);

    // Update React State
    setJourneys(journeysCopy); // update journeys state
    setJourneySteps(journeyStepsCopy); // update steps state
  };

  //this is to update the step removal while performing Edit and Skip task
  const handleTaskRemovalStateUpdate = (stepId, filteredTasks) => {
    const journeyStepsCopy = [...journeySteps];

    journeyStepsCopy.forEach((j) => {
      if (j.id === stepId) j.tasks = filteredTasks;
    });

    setJourneySteps(journeyStepsCopy);
  };

  const handleTaskUpdateStateUpdate = (task) => {
    const journeyStepsCopy = [...journeySteps];

    journeyStepsCopy.forEach((j) => {
      j.tasks.forEach((t) => {
        if (t.id === task.id) {
          t = task;
        }
      });
    });

    setJourneySteps(journeyStepsCopy);
  };

  // This function should be removed and replaced with refetchJourneys
  const fetchJourneysAgainAfterDelay = () => {
    setTimeout(() => {
      setRefetchJourneys(true);
    }, 1000);
  };

  return {
    journeyGroups,
    selectedJourneyGroup,
    setSelectedJourneyGroup,

    journeys,
    fetchJourneysAgainAfterDelay,
    loadingJourneys,
    refetchJourneys: () => setRefetchJourneys(true),

    journeySteps,
    setRefetchJourneySteps,
    refetchJourneySteps: () => setRefetchJourneySteps(true),

    handleTaskCompletionStateUpdate,

    handleTaskRemovalStateUpdate,
    handleTaskUpdateStateUpdate,

    selectedJourney,
    setSelectedJourney,
    journeysForMultipleDates,

    selectedDueDate,
    setSelectedDueDate,

    selectedTask,
    setSelectedTask,

    onStageWithTable,
    renderEmptyState,

    closePrevDrawer,
    setClosePrevDrawer,
  };
};

export default useJourneys;
